if (!customElements.get('mix-and-match')) {
  class MixAndMatch extends HTMLElement {
    constructor() {
      super();
    }

    get mainContainer() {
      return this.closest('[data-mix-and-match]');
    }

    get productTemplate() {
      return this.querySelector('template');
    }

    get productsContainer() {
      return this.querySelector('[data-mix-and-match-products-list]');
    }

    get progressBar() {
      return this.querySelector('.mix-and-match-progress-bar');
    }

    get mixAndMatchProducts() {
      const script = this.querySelector('script[type="application/json"]');
      return script ? JSON.parse(script.innerHTML) : [];
    }

    get getMixAndMatchModal() {
      return document.querySelector('modal-dialog#PopupModal-mix-and-match');
    }

    async connectedCallback() {
      await this.updateMixAndMatch();
      await this.onCartUpdate();
    }

    getSectionInnerHTML(html, selector) {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }

    async updateMixAndMatch() {
      const addedProducts = await this.getAddedProducts();
      if (addedProducts.length < 1) {
        this.classList.add('no-products');
        if (this.mainContainer) this.mainContainer.classList.remove('mix-and-match');
      } else {
        this.classList.remove('no-products');
        if (this.mainContainer) this.mainContainer.classList.add('mix-and-match');
        this.buildEmptyProductVariants();
        await this.buildProductVariants(addedProducts);
        await this.updateProgressBar(addedProducts);
      }
    }

    async updateMixAndMatchPopup() {
      const modalIsOpen = this.getMixAndMatchModal?.hasAttribute('open');
      if (!modalIsOpen) {
        const productCards = this.getMixAndMatchModal.querySelectorAll('product-card');
        productCards.forEach((productCard) => {
          productCard.connectedCallback();
        });
      }
    }

    async getAddedProducts() {
      const cartItems = await this.getCartItems();
      return cartItems
        .filter((item) => {
          const itemProperties = item.properties;
          if (!itemProperties) return;
          const wasItAddedInMixAndMatch = itemProperties['_mix_match_enabled'];
          if (!wasItAddedInMixAndMatch) return;
          const wasItAddedInMixAndMatchVal = wasItAddedInMixAndMatch === 'true' || false;
          if (!wasItAddedInMixAndMatchVal) return;
          return item;
        })
        .filter((item) => this.mixAndMatchProducts.some((product) => product.id === item.product_id))
        .map((item) => {
          let productSubtext = '';
          const cartDrawer = document.getElementById('Cart-Drawer');
          if (cartDrawer) {
            const lineItem = cartDrawer.querySelector(`.product-cart-item[data-id="${item.id}"]`);
            if (lineItem) productSubtext = lineItem.dataset.subtext;
          }
          return {
            id: item.id,
            key: item.key,
            product_id: item.product_id,
            title: item.product_title,
            price: item.price,
            discounted_price: item.discounted_price,
            image: item.image,
            handle: item.handle,
            quantity: item.quantity,
            subtext: productSubtext,
          };
        });
    }

    async getCartItems() {
      try {
        const response = await fetch('/cart.js');
        if (!response.ok) throw new Error('Failed to fetch cart');
        const cart = await response.json();
        return cart.items || [];
      } catch (error) {
        console.error('Error fetching cart:', error);
        return [];
      }
    }

    buildEmptyProductVariants() {
      const fragment = document.createDocumentFragment();
      this.productsContainer.innerHTML = '';
      for (let index = 0; index < 3; index++) {
        const product = this.productTemplate.content.cloneNode(true);

        fragment.appendChild(product);
      }
      this.productsContainer.appendChild(fragment);
    }

    async buildProductVariants(addedProducts) {
      const fragment = document.createDocumentFragment();
      this.productsContainer.innerHTML = '';
      let totalAppended = 0;
      let format = window.theme.settings.money_with_currency_format || '{{amount}} kr.';

      addedProducts.forEach((product) => {
        const productTemplate = this.productTemplate.content.cloneNode(true);
        if (!productTemplate) return;
        const productWrapper = productTemplate.querySelector('.mix-and-match-product');
        const productImage = productTemplate.querySelector('.mix-and-match-product-image > img');
        const productTitle = productTemplate.querySelector('.mix-and-match-product-title');
        const productPrice = productTemplate.querySelector('.mix-and-match-product-price');
        const productDiscountedPrice = productTemplate.querySelector('.mix-and-match-product-discounted-price');
        const productCheckmark = productTemplate.querySelector('button.mix-and-match-checkmark');
        const productQuantity = productTemplate.querySelector('.mix-and-match-product-quantity');
        const productUpsellHeading = productTemplate.querySelector('.mix-and-match-product-description-bottom');
        try {
          if (productCheckmark) {
            productCheckmark.addEventListener('click', async () => {
              if (productWrapper) productWrapper.classList.add('mix-and-match-product-disabled');
              dispatchCustomEvent('line-item:change:start', {
                quantity: 0,
              });
              fetch(theme.routes.cart_change_url, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Accept: 'application/json',
                },
                body: JSON.stringify({
                  id: product.key,
                  quantity: 0,
                  sections: this.getSectionsToRender().map((section) => section.section),
                  sections_url: window.location.pathname,
                }),
              })
                .then((response) => {
                  if (response.ok && productWrapper) productWrapper.classList.remove('mix-and-match-product-disabled');
                  return response.json();
                })
                .then(async (parsedState) => {
                  let isCartDrawerInitialized = false;
                  this.getSectionsToRender().forEach((section) => {
                    const elementToReplace = document.getElementById(section.id)?.querySelector(section.selector) || document.getElementById(section.id);
                    if (elementToReplace && parsedState && parsedState.sections && parsedState.sections[section.section]) {
                      elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
                      if (typeof CartDrawer !== 'undefined') {
                        if (!isCartDrawerInitialized) {
                          new CartDrawer();
                          isCartDrawerInitialized = true;
                        }
                      }
                    }
                  });

                  dispatchCustomEvent('line-item:change:end', {
                    quantity: 0,
                    cart: parsedState,
                  });
                });
            });
          }
        } catch (error) {
          console.error('Error building product variant:', error);
        }

        if (productImage) productImage.src = product.image;
        if (productTitle) productTitle.textContent = product.title;
        if (productPrice) productPrice.textContent = formatMoney(product.price, format);
        if (productQuantity) {
          if (product.quantity > 1) {
            productQuantity.classList.remove('is-hidden');
            productQuantity.textContent = `x ${product.quantity}`;
          } else {
            productQuantity.classList.add('is-hidden');
            productQuantity.textContent = '';
          }
        }
        if (productDiscountedPrice) {
          if (product.discounted_price !== product.price) {
            if (productPrice) productPrice.classList.add('mix-and-match-product-price--discounted');
            productDiscountedPrice.textContent = formatMoney(product.discounted_price, format);
            productDiscountedPrice.classList.remove('is-hidden');
          } else {
            productDiscountedPrice.classList.add('is-hidden');
          }
        }
        if (productUpsellHeading) {
          if (product.subtext) {
            productUpsellHeading.classList.remove('is-hidden');
            productUpsellHeading.textContent = product.subtext;
          } else {
            productUpsellHeading.classList.add('is-hidden');
          }
        }
        if (productWrapper) productWrapper.classList.remove('no-product');

        fragment.appendChild(productTemplate);
        totalAppended++;
      });
      while (totalAppended < 3) {
        const emptyProduct = this.productTemplate.content.cloneNode(true);
        fragment.appendChild(emptyProduct);
        totalAppended++;
      }
      this.productsContainer.appendChild(fragment);
    }

    getSectionsToRender() {
      return [
        {
          id: 'Cart',
          section: 'main-cart',
          selector: '.thb-cart-form',
        },
        {
          id: 'Cart-Drawer',
          section: 'cart-drawer',
          selector: '.cart-drawer',
        },
        {
          id: 'cart-drawer-toggle',
          section: 'cart-bubble',
          selector: '.thb-item-count',
        },
        {
          id: 'PopupModal-mix-and-match',
          section: 'mix-and-match-drawer',
          selector: '.mix-and-match-modal__content-info-list',
        },
      ];
    }

    async updateProgressBar(addedProducts) {
      const quantity = Math.min(
        3,
        addedProducts.reduce((acc, variant) => acc + variant.quantity, 0)
      );

      const progressBarLabels = this.progressBar.querySelectorAll('.mix-and-match-progress-bar-label');
      progressBarLabels.forEach((label) => {
        label.classList.remove('active');
        const activeLabel = label.hasAttribute(`data-progress-${quantity}`);
        if (activeLabel) label.classList.add('active');
      });
    }

    async onCartUpdate() {
      if (this._fetchIntercepted) return;
      this._fetchIntercepted = true;

      const _this = this;

      (function () {
        const originalFetch = window.fetch;
        window.fetch = async function (...args) {
          const response = await originalFetch.apply(this, args);
          if (args[0].includes('/cart/')) {
            await _this.updateMixAndMatch();
            await _this.updateMixAndMatchPopup();
          }
          return response;
        };
      })();
    }
  }
  customElements.define('mix-and-match', MixAndMatch);
}
